import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Card, CardContent } from "@/components/ui/card";
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid, Cell,
} from "recharts";
import { LEAD_STATUSES, statusByValue } from "@/lib/constants";
import { Loader2 } from "lucide-react";

export default function Reports() {
  const [growth, setGrowth] = useState([]);
  const [perf, setPerf] = useState([]);
  const [funnel, setFunnel] = useState([]);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    Promise.all([
      api.get("/reports/contacts-growth?days=30"),
      api.get("/reports/broadcast-performance"),
      api.get("/reports/conversion-funnel"),
      api.get("/dashboard/stats"),
    ]).then(([g, p, f, s]) => {
      setGrowth(g.data.series || []);
      setPerf(p.data || []);
      setFunnel(f.data || []);
      setStats(s.data);
    });
  }, []);

  if (!stats) return <div className="p-8"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>;

  const perfChart = perf.map((p) => ({
    name: p.name?.slice(0, 12) || "—",
    sent: p.stats?.sent || 0,
    delivered: p.stats?.delivered || 0,
    read: p.stats?.read || 0,
  }));

  const maxFunnel = Math.max(...funnel.map((f) => f.count), 1);

  return (
    <div className="p-6 md:p-8 max-w-[1500px] mx-auto" data-testid="reports-page">
      <PageHeader title="Reports" description="Performance and growth analytics" />

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <Card className="border-slate-200 shadow-none">
          <CardContent className="p-6">
            <div className="label-caps">Delivery rate</div>
            <div className="data-value mt-1">{stats.delivery_rate}%</div>
          </CardContent>
        </Card>
        <Card className="border-slate-200 shadow-none">
          <CardContent className="p-6">
            <div className="label-caps">Reply rate</div>
            <div className="data-value mt-1">{stats.reply_rate}%</div>
          </CardContent>
        </Card>
        <Card className="border-slate-200 shadow-none">
          <CardContent className="p-6">
            <div className="label-caps">Conversion rate</div>
            <div className="data-value mt-1">{stats.conversion_rate}%</div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="border-slate-200 shadow-none">
          <CardContent className="p-6">
            <div className="label-caps mb-3">Contacts growth (30 days)</div>
            <div className="h-72" style={{ minHeight: 288, minWidth: 0 }}>
              <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={288}>
                <LineChart data={growth} margin={{ top: 8, right: 8, bottom: 0, left: -16 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                  <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#64748B" }} tickFormatter={(d) => d?.slice(5)} />
                  <YAxis tick={{ fontSize: 11, fill: "#64748B" }} allowDecimals={false} />
                  <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #E2E8F0", fontSize: 12 }} />
                  <Line type="monotone" dataKey="count" stroke="#25D366" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200 shadow-none">
          <CardContent className="p-6">
            <div className="label-caps mb-3">Broadcast performance</div>
            {perfChart.length === 0 ? (
              <div className="h-72 grid place-items-center text-sm text-slate-400">No broadcasts sent yet</div>
            ) : (
              <div className="h-72" style={{ minHeight: 288, minWidth: 0 }}>
                <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={288}>
                  <BarChart data={perfChart} margin={{ top: 8, right: 8, bottom: 0, left: -16 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                    <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#64748B" }} />
                    <YAxis tick={{ fontSize: 11, fill: "#64748B" }} />
                    <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #E2E8F0", fontSize: 12 }} />
                    <Bar dataKey="sent" fill="#94A3B8" />
                    <Bar dataKey="delivered" fill="#25D366" />
                    <Bar dataKey="read" fill="#10B981" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="border-slate-200 shadow-none lg:col-span-2">
          <CardContent className="p-6">
            <div className="label-caps mb-3">Conversion funnel</div>
            <div className="space-y-2">
              {funnel.map((f) => {
                const status = statusByValue(f.status);
                const width = (f.count / maxFunnel) * 100;
                return (
                  <div key={f.status} className="flex items-center gap-3">
                    <div className="w-36 text-sm text-slate-700">{status.label}</div>
                    <div className="flex-1 bg-slate-100 h-7 rounded relative">
                      <div
                        className="h-full bg-brand rounded transition-all"
                        style={{ width: `${width}%` }}
                      />
                      <div className="absolute inset-0 flex items-center px-2 text-xs font-medium text-slate-900">
                        {f.count}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
